/**
 * Template Library Component
 * 
 * Displays all templates (built-in + approved) with edit/delete capabilities.
 * Allows admin to manage the template library.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Flex, Text, Button, Badge, Dialog, Heading, TextField } from '@radix-ui/themes';
import { DeviceTemplate } from '../../types';
import { templatesAPI, adminAPI } from '../../services/api';
import { DEVICE_TYPE_LABELS } from '../../config/constants';
import TemplateFormFields from '../TemplateFormFields';

interface TemplateLibraryProps {
  adminKey: string;
  onUpdate?: () => void;
}

const TemplateLibrary: React.FC<TemplateLibraryProps> = ({ adminKey, onUpdate }) => {
  const [templates, setTemplates] = useState<DeviceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<DeviceTemplate | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<DeviceTemplate>>({});

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const allTemplates = await templatesAPI.getAll();
      // Only show approved templates (built-in templates have status 'approved')
      const approvedTemplates = allTemplates.filter(t => t.status === 'approved');
      setTemplates(approvedTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
      alert('❌ Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter and sort templates alphabetically
  const filteredTemplates = React.useMemo(() => {
    let filtered = templates;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.brand?.toLowerCase().includes(query) ||
        t.model?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query)
      );
    }
    
    // Sort alphabetically by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [templates, searchQuery]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleEdit = (template: DeviceTemplate) => {
    setEditingTemplate(template);
    setEditFormData({
      name: template.name,
      type: template.type,
      brand: template.brand,
      model: template.model,
      description: template.description,
      powerInput: template.powerInput,
      powerOutputs: template.powerOutputs || [],
      batteryCapacity: template.batteryCapacity,
      usbInputs: template.usbInputs || [],
      usbOutputs: template.usbOutputs || [],
    });
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    try {
      await adminAPI.updateTemplate(editingTemplate.id, editFormData as DeviceTemplate, adminKey);
      setEditingTemplate(null);
      setEditFormData({});
      await loadTemplates();
      onUpdate?.();
      alert('✅ Template updated successfully!');
    } catch (error: any) {
      console.error('Failed to update template:', error);
      alert(`❌ Failed to update template: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDelete = async (templateId: string, templateName: string) => {
    if (!window.confirm(`⚠️ Are you sure you want to delete "${templateName}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      await adminAPI.deleteTemplate(templateId, adminKey);
      await loadTemplates();
      onUpdate?.();
      alert('✅ Template deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete template:', error);
      alert(`❌ Failed to delete template: ${error.message || 'Unknown error'}`);
    }
  };

  if (loading) {
    return <Text>Loading templates...</Text>;
  }

  return (
    <div>
      <Flex direction="column" gap="3">
        {/* Search Input */}
        <TextField.Root
          placeholder="🔍 Search templates by name, brand, model, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="3"
        />
        
        {templates.length === 0 ? (
          <Text color="gray">No templates in library yet.</Text>
        ) : filteredTemplates.length === 0 ? (
          <Text color="gray">No templates match your search.</Text>
        ) : (
          filteredTemplates.map(template => (
            <Card key={template.id}>
              <Flex direction="column" gap="2">
                <Flex justify="between" align="center">
                  <Flex direction="column" gap="1">
                    <Flex align="center" gap="2">
                      <Heading size="3">{template.name}</Heading>
                      <Badge color="violet" size="1">
                        {DEVICE_TYPE_LABELS[template.type]}
                      </Badge>
                      {template.submittedBy && (
                        <Badge color="gray" size="1">User Contributed</Badge>
                      )}
                    </Flex>
                    {(template.brand || template.model) && (
                      <Text size="2" color="gray">
                        {template.brand && template.model 
                          ? `${template.brand} ${template.model}`
                          : template.brand || template.model
                        }
                      </Text>
                    )}
                  </Flex>
                  <Flex gap="2">
                    <Button variant="soft" onClick={() => handleEdit(template)}>
                      Edit
                    </Button>
                    <Button 
                      variant="soft" 
                      color="red"
                      disabled={template.isBuiltIn}
                      onClick={() => handleDelete(template.id, template.name)}
                      title={template.isBuiltIn ? 'Built-in templates cannot be deleted' : 'Delete this template'}
                    >
                      Delete
                    </Button>
                  </Flex>
                </Flex>
                
                {template.description && (
                  <Text size="2" color="gray">{template.description}</Text>
                )}

                <Flex gap="3" wrap="wrap">
                  {template.powerInput && (
                    <Badge color="blue" size="1">
                      📥 {template.powerInput.voltage}V @ {template.powerInput.maxCurrent}A
                    </Badge>
                  )}
                  {template.powerOutputs && template.powerOutputs.length > 0 && (
                    <Badge color="green" size="1">
                      📤 {template.powerOutputs.reduce((sum, port) => sum + port.portCount, 0)} Power Output{template.powerOutputs.reduce((sum, port) => sum + port.portCount, 0) !== 1 ? 's' : ''}
                    </Badge>
                  )}
                  {template.batteryCapacity && (
                    <Badge color="orange" size="1">
                      🔋 {template.batteryCapacity.capacityWh}Wh
                    </Badge>
                  )}
                  {template.usbInputs && template.usbInputs.length > 0 && (
                    <Badge color="purple" size="1">
                      🔌 {template.usbInputs.reduce((sum, port) => sum + port.count, 0)} USB In
                    </Badge>
                  )}
                  {template.usbOutputs && template.usbOutputs.length > 0 && (
                    <Badge color="purple" size="1">
                      🔌 {template.usbOutputs.reduce((sum, port) => sum + port.count, 0)} USB Out
                    </Badge>
                  )}
                </Flex>
              </Flex>
            </Card>
          ))
        )}
      </Flex>

      {/* Edit Dialog */}
      <Dialog.Root open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
        <Dialog.Content style={{ maxWidth: 650, maxHeight: '90vh', overflowY: 'auto' }}>
          <Dialog.Title>Edit Template</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Update template details. Changes will affect all future devices using this template.
          </Dialog.Description>

          {editingTemplate && (
            <TemplateFormFields
              template={editFormData}
              onChange={(updated) => setEditFormData(prev => ({ ...prev, ...updated }))}
            />
          )}

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
};

export default TemplateLibrary;
